import { useState, useEffect, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';

export type PurchasableModule = 'hard' | 'roulette' | 'coquin';

const PRODUCT_IDS: Record<PurchasableModule, string> = {
  hard: 'com.app.spindrink.hard',
  roulette: 'com.app.spindrink.roulette',
  coquin: 'com.app.spindrink.coquin',
};

const STORAGE_KEY = 'purchased_modules';

export function usePurchases() {
  const [purchasedModules, setPurchasedModules] = useState<Set<PurchasableModule>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      const { value } = await Preferences.get({ key: STORAGE_KEY });
      if (value) {
        const modules = JSON.parse(value) as PurchasableModule[];
        setPurchasedModules(new Set(modules));
      }
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePurchases = async (modules: Set<PurchasableModule>) => {
    try {
      await Preferences.set({
        key: STORAGE_KEY,
        value: JSON.stringify(Array.from(modules)),
      });
    } catch (error) {
      console.error('Error saving purchases:', error);
    }
  };

  const isModulePurchased = useCallback((module: PurchasableModule): boolean => {
    return purchasedModules.has(module);
  }, [purchasedModules]);

  const purchaseModule = useCallback(async (module: PurchasableModule): Promise<boolean> => {
    try {
      // In production, this would use StoreKit via a Capacitor plugin:
      // const result = await InAppPurchase.purchase({ productId: PRODUCT_IDS[module] });
      // For now, simulate a successful purchase for development
      console.log(`Purchasing product: ${PRODUCT_IDS[module]}`);

      const newModules = new Set(purchasedModules);
      newModules.add(module);
      setPurchasedModules(newModules);
      await savePurchases(newModules);
      return true;
    } catch (error) {
      console.error('Purchase failed:', error);
      return false;
    }
  }, [purchasedModules]);

  const restorePurchases = useCallback(async (): Promise<boolean> => {
    try {
      // In production, this would restore via StoreKit:
      // const result = await InAppPurchase.restorePurchases();
      console.log('Restoring purchases...');
      await loadPurchases();
      return true;
    } catch (error) {
      console.error('Restore failed:', error);
      return false;
    }
  }, []);

  return {
    purchasedModules,
    isModulePurchased,
    purchaseModule,
    restorePurchases,
    loading,
    productIds: PRODUCT_IDS,
  };
}
